import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, Check } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { haptic } from '../../lib/native'
import { VoxnelaMark } from '../../components/ui/VoxnelaMark'
import { useUiText } from '../../i18n/ui'
import { studioIdeasOnboardingCopy } from '../../i18n/studioQuickStartIdeas'
import { onboardingShowcaseCopy, type OnboardingShowcaseKey } from '../../i18n/onboardingShowcase'
import { OnboardingIdeaCollage } from './OnboardingIdeaCollage'

export const VIDEO_ONBOARDING_STORAGE_KEY = 'voxnela:video-onboarding:v7'

const slides = [
  {
    key: 'face',
    images: [
      '/tasks/voxnela-signature/face-style-analysis.png',
      '/tasks/voxnela-signature/profile-photo-analysis.png',
    ],
  },
  {
    key: 'time',
    images: ['/tasks/time-traveler/belle-epoque-paris.webp'],
  },
  {
    key: 'outfit',
    images: [
      '/tasks/fashion_tryon_editorial.webp',
      '/tasks/voxnela-signature/combin-analysis.png',
    ],
  },
  {
    key: 'decor',
    images: [
      '/tasks/interior_architectural_concept.webp',
      '/tasks/voxnela-signature/room-analysis.png',
    ],
  },
  {
    key: 'pixar',
    images: ['/tasks/pixar_3d_avatar.webp', '/tasks/claymation_stop_motion.webp'],
  },
  {
    key: 'area',
    images: [
      '/creation-style-samples/chart/area.webp',
      '/tasks/business-computer-repair-blueprint.webp',
    ],
  },
] as const satisfies ReadonlyArray<{ key: OnboardingShowcaseKey; images: readonly string[] }>

const onboardingPageCount = slides.length + 1

export function VideoOnboarding({
  forceOpen = false,
  onVisibilityChange,
  onComplete,
}: {
  forceOpen?: boolean
  onVisibilityChange?: (visible: boolean) => void
  onComplete?: () => void
}) {
  const { text, locale } = useUiText()
  const reduceMotion = useReducedMotion()
  const [open, setOpen] = useState(() => forceOpen || localStorage.getItem(VIDEO_ONBOARDING_STORAGE_KEY) !== 'done')
  const [index, setIndex] = useState(0)
  const preloadedImages = useRef<HTMLImageElement[]>([])

  useEffect(() => {
    onVisibilityChange?.(open)
  }, [onVisibilityChange, open])

  useEffect(() => {
    if (!open || preloadedImages.current.length > 0) return
    preloadedImages.current = slides.flatMap(({ images }) => images.map((src) => {
      const image = new Image()
      image.src = src
      return image
    }))
  }, [open])

  const finish = () => {
    localStorage.setItem(VIDEO_ONBOARDING_STORAGE_KEY, 'done')
    setOpen(false)
    onComplete?.()
    void haptic('selection')
  }

  const next = () => {
    if (index === onboardingPageCount - 1) {
      finish()
      return
    }
    setIndex((current) => current + 1)
    void haptic('selection')
  }

  const goTo = (nextIndex: number) => {
    if (nextIndex === index) return
    setIndex(nextIndex)
    void haptic('selection')
  }

  const isIdeasSlide = index === 0
  const slide = slides[Math.max(0, index - 1)]!
  const ideasCopy = studioIdeasOnboardingCopy[locale]
  const showcaseCopy = onboardingShowcaseCopy[locale][slide.key]

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={`video-onboarding${isIdeasSlide ? ' is-ideas-page' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={text('onboardingWelcome')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="video-onboarding-ambient" aria-hidden="true" />
          <header>
            <span className="video-onboarding-brand"><VoxnelaMark /><strong>Voxnela</strong></span>
          </header>

          <article className={`video-onboarding-slide${isIdeasSlide ? ' video-onboarding-ideas-slide' : ''}`}>
            {isIdeasSlide ? (
              <>
                <OnboardingIdeaCollage locale={locale} />
                <motion.div
                  className="video-onboarding-copy"
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.24 }}
                >
                  <h1>{ideasCopy.title}</h1>
                  <p>{ideasCopy.body}</p>
                </motion.div>
              </>
            ) : (
              <>
                <div className={`video-onboarding-visual is-${slide.key}${slide.images.length > 1 ? ' is-multi' : ''}`}>
                  <div className={`video-onboarding-image-group is-${slide.key}${slide.images.length > 1 ? ' is-multi' : ''}`}>
                    {slide.images.map((image, imageIndex) => (
                      <img
                        key={image}
                        src={image}
                        alt={imageIndex === 0 ? showcaseCopy.title : `${showcaseCopy.title} ${imageIndex + 1}`}
                      />
                    ))}
                  </div>
                </div>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={slide.key}
                    className="video-onboarding-copy"
                    initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: reduceMotion ? 0 : -6 }}
                    transition={{ duration: reduceMotion ? 0.12 : 0.24 }}
                  >
                    <h1>{showcaseCopy.title}</h1>
                    <p>{showcaseCopy.body}</p>
                  </motion.div>
                </AnimatePresence>
              </>
            )}
          </article>

          <footer>
            <div className="video-onboarding-dots" aria-label={`${index + 1} / ${onboardingPageCount}`}>
              {Array.from({ length: onboardingPageCount }, (_, dotIndex) => (
                <button
                  key={dotIndex}
                  type="button"
                  className={dotIndex === index ? 'is-active' : undefined}
                  onClick={() => goTo(dotIndex)}
                  aria-label={`${dotIndex + 1} / ${onboardingPageCount}`}
                />
              ))}
            </div>
            <button className="video-onboarding-next" type="button" onClick={next}>
              <span>{index === onboardingPageCount - 1 ? text('startCreating') : text('continue')}</span>
              {index === onboardingPageCount - 1 ? <Check size={19} /> : <ArrowRight size={19} />}
            </button>
          </footer>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
